import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Solutions",
      links: [
        { label: "Live Webinars", path: "/webinars/live", icon: "Video" },
        { label: "On-Demand Courses", path: "/webinars/recorded", icon: "Play" },
        { label: "Enterprise Platform", path: "/enterprise", icon: "Award" },
        { label: "Create Account", path: "/register", icon: "UserPlus" }
      ]
    },
    {
      title: "Resources",
      links: [
        { label: "About Us", path: "/about", icon: "Info" },
        { label: "Contact Sales", path: "/contact", icon: "MessageCircle" }
      ]
    },
    {
      title: "Compliance",
      links: [
        { label: "Terms & Conditions", path: "/terms-conditions" },
        { label: "Privacy Policy", path: "/privacy-policy" },
        { label: "Refund Policy", path: "/refund-cancellation-policy" },
        { label: "Shipping & Returns", path: "/shipping-return-policy" }
      ]
    }
  ];

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <footer className="bg-gradient-to-br from-blue-950 via-indigo-900 to-blue-900 text-white relative overflow-hidden z-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, rgba(56,189,248,0.25) 1px, transparent 0)",
            backgroundSize: "44px 44px"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-14 sm:py-16 border-b border-blue-800/50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-14">
            {/* Logo/Brand */}
            <div className="lg:col-span-2 flex flex-col">
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/assets/logo.png"
                  alt="DailyRespond Logo"
                  className="h-11 w-auto object-contain drop-shadow"
                />
                <span className="text-2xl font-black bg-gradient-to-r from-cyan-300 via-sky-300 to-teal-300 bg-clip-text text-transparent drop-shadow">
                  DailyRespond
                </span>
              </div>
              <p className="text-sm text-blue-100/80 mb-5 max-w-md">
                Supercharge your team’s compliance with secure, expert-led training.<br /> DailyRespond keeps you audit-ready and employee-certified, at scale.
              </p>
              
            </div>

            {/* Footer Links */}
            {footerSections.map((section) => (
              <div key={section.title}>
                <h4 className="text-lg font-semibold mb-4 text-cyan-400 tracking-wide">
                  {section.title}
                </h4>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <button
                        onClick={() => handleLinkClick(link.path)}
                        className="text-sm text-blue-100/90 hover:text-cyan-400 transition flex items-center gap-2 group font-medium px-1 py-1"
                      >
                        {link.icon && (
                          <Icon
                            name={link.icon}
                            size={15}
                            className="text-cyan-500 group-hover:text-cyan-400"
                          />
                        )}
                        <span>{link.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications/Trust */}
        <div className="py-7 border-b border-blue-800/50 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex flex-wrap items-center gap-8 justify-center">
            <div className="flex items-center gap-2 text-blue-200">
              <Icon name="Shield" size={18} className="text-cyan-400" />
              <span className="text-xs font-medium">SHRM Approved</span>
            </div>
            <div className="flex items-center gap-2 text-blue-200">
              <Icon name="Award" size={18} className="text-teal-400" />
              <span className="text-xs font-medium">HRCI Certified</span>
            </div>
            <div className="flex items-center gap-2 text-blue-200">
              <Icon name="CheckCircle" size={18} className="text-cyan-400" />
              <span className="text-xs font-medium">ISO 9001 Compliant</span>
            </div>
          </div>
          <div className="text-xs text-blue-300 font-medium mt-3 sm:mt-0 hidden sm:block">
            Secure SSL | <span className="text-cyan-300">US/EU Data Center</span>
          </div>
        </div>

        {/* Contact/Site Details */}
        <div className="py-7 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex gap-8 flex-col sm:flex-row">
            <div className="flex items-start gap-3">
              <Icon name="Mail" size={18} className="text-cyan-400" />
              <span className="text-sm">
                <span className="block text-cyan-200 font-bold">Mail Us:</span>
                <a href="mailto:support@dailyrespond.com" className="text-cyan-200 hover:text-cyan-400 underline">
                  support@dailyrespond.com
                </a>
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="MapPin" size={18} className="text-blue-300 mt-1" />
              <span className="text-sm">
                <span className="block text-cyan-200 font-bold">Reach Us:</span>
                <span className="text-blue-100/90">
                  8506 Madison Ave<br />
                  Suite A<br />
                  Fair Oaks, CA 95628<br />
                  United States
                     {/* 2/24, East Street,Thirumazhapadi(PO)<br />
               Ariyalur (DT),Tamilnadu-621851<br /> */}
                </span>
              </span>
            </div>
          </div>
          <div className="mt-3 sm:mt-0 text-sm text-blue-100/80 text-center">
            Open Mon-Fri 9am-6pm PT &nbsp;|&nbsp; <span className="text-cyan-100">Live Chat</span>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-blue-800/50 py-5">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3">
            <div className="text-xs text-blue-200 text-center md:text-left">
              © {currentYear} DailyRespond. Compliance made simple, secure, and certified.
              {/* © {currentYear}  MRTrixx Services LLP. Compliance made simple, secure, and certified. */}
            
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Blobs */}
      <div className="absolute bottom-0 left-0 w-52 h-52 bg-gradient-to-tr from-cyan-400/10 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-indigo-400/10 to-transparent rounded-full blur-3xl pointer-events-none" />
    </footer>
  );
};

export default Footer;
